import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    useGetApiUserPreferencesParsedQuery,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import { GetProjectApiResponse } from 'store/userOrg/userOrgApi';
import { resetActiveProject, ActiveProjectKey } from 'store/additional/activeProjectsSlice';

export const useGlobalFilterSelection = (projects: GetProjectApiResponse | undefined) => {
    const dispatch = useDispatch();
    const { data: preferences, isLoading: preferencesIsLoading } = useGetApiUserPreferencesParsedQuery();
    const [putPreferences, { isLoading: putPreferencesIsLoading }] = usePutApiUserPreferencesMutation();

    const selectedDDIDashboardTabProject = useSelector((state: RootState) => state.activeProjects.ddiDashboard);
    const selectedWorkflowsDashboardTabProject = useSelector(
        (state: RootState) => state.activeProjects.workflowsDashboard
    );
    const selectedContainersTabProject = useSelector((state: RootState) => state.activeProjects.containers);
    const selectedDatasetsTabProject = useSelector((state: RootState) => state.activeProjects.datasets);
    const selectedWorkflowsTabProject = useSelector((state: RootState) => state.activeProjects.workflows);
    const selectedTransferStatusesTabProject = useSelector((state: RootState) => state.activeProjects.transferStatuses);
    const selectedHpcTabProject = useSelector((state: RootState) => state.activeProjects.hpc);

    const selectedGlobalFilterValues = preferences?.globalFilterValues ?? [];

    const handleSetGlobalFilter = (projectShortName: string) => {
        const selectedGlobalFilterShortNamesCopy = selectedGlobalFilterValues ? [...selectedGlobalFilterValues] : [];

        // If projectId is not included and not 'null' or 'all', add it to globalFilterValues
        if (
            !selectedGlobalFilterShortNamesCopy.includes(projectShortName) &&
            projectShortName !== 'null' &&
            projectShortName !== 'all'
        ) {
            putPreferences({
                body: { ...preferences, globalFilterValues: [...selectedGlobalFilterShortNamesCopy, projectShortName] },
            });
        }

        // If current projectId is included, remove it from globalFilterValues
        if (selectedGlobalFilterShortNamesCopy.includes(projectShortName)) {
            const newValues = selectedGlobalFilterShortNamesCopy.filter(element => element !== projectShortName);
            putPreferences({
                body: {
                    ...preferences,
                    globalFilterValues: newValues,
                },
            })
                .unwrap()
                .then(() => {
                    projectShortName === selectedDDIDashboardTabProject &&
                        dispatch(resetActiveProject(ActiveProjectKey.DdiDashboard));
                    projectShortName === selectedWorkflowsDashboardTabProject &&
                        dispatch(resetActiveProject(ActiveProjectKey.WorkflowsDashboard));
                    projectShortName === selectedDatasetsTabProject && dispatch(resetActiveProject(ActiveProjectKey.Datasets));
                    projectShortName === selectedContainersTabProject && dispatch(resetActiveProject(ActiveProjectKey.Containers));
                    projectShortName === selectedWorkflowsTabProject && dispatch(resetActiveProject(ActiveProjectKey.Workflows));
                    projectShortName === selectedTransferStatusesTabProject &&
                        dispatch(resetActiveProject(ActiveProjectKey.TransferStatuses));
                    projectShortName === selectedHpcTabProject && dispatch(resetActiveProject(ActiveProjectKey.Hpc));
                });
        }

        // If projectId is 'all', set globalFilterValues to all project IDs
        if (projectShortName === 'all') {
            const allShortNames =
                projects?.map(project => project?.ShortName).filter((v): v is string => typeof v === 'string') || [];
            putPreferences({
                body: {
                    ...preferences,
                    globalFilterValues: allShortNames,
                },
            });
        }

        // If projectId is 'null', set globalFilterValues based on conditions
        if (projectShortName === 'null') {
            putPreferences({
                body: {
                    ...preferences,
                    globalFilterValues: [],
                },
            })
                .unwrap()
                .then(() => {
                    dispatch(resetActiveProject(ActiveProjectKey.DdiDashboard));
                    dispatch(resetActiveProject(ActiveProjectKey.WorkflowsDashboard));
                    dispatch(resetActiveProject(ActiveProjectKey.Workflows));
                    dispatch(resetActiveProject(ActiveProjectKey.Containers));
                    dispatch(resetActiveProject(ActiveProjectKey.Datasets));
                    dispatch(resetActiveProject(ActiveProjectKey.Hpc));

                    selectedTransferStatusesTabProject !== 'All' && dispatch(resetActiveProject(ActiveProjectKey.TransferStatuses));

                    selectedDDIDashboardTabProject !== 'All' && dispatch(resetActiveProject(ActiveProjectKey.DdiDashboard));
                });
        }
    };

    const handleSetGlobalFilters = (projectShortNames: string[]) => {
        putPreferences({
            body: {
                ...preferences,
                globalFilterValues: projectShortNames,
            },
        });
    };

    return {
        selectedGlobalFilterValues,
        setGlobalFilter: handleSetGlobalFilter,
        setGlobalFilters: handleSetGlobalFilters,
        preferencesIsLoading,
        putPreferencesIsLoading,
    };
};
